import { useMelwaysLink } from '@seek/melways-react';
import { ProfileVisibilityField2 } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Box, Card, Heading, Stack, Text, TextLink } from 'braid-design-system';
import { useEffect } from 'react';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { useEffectOnce } from 'react-use';

import { useConfig } from 'src/context/config';
import { Page, PageSection, useTrackingClient } from 'src/context/tracking';
import { useEnhancedHubble } from 'src/hooks/useEnhancedHubble';
import { useSetHubblePage } from 'src/modules/hubble';

import { useProfileVisibilityOptions } from '../../hooks/useProfileVisibilityOptions/useProfileVisibilityOptions';

import translations from './.vocab';

interface ProfileVisibilityProps {
  onChange: (value: any) => void;
  privacyLevelMessage?: string;
  value?: any;
  withCardPromote?: boolean;
  showHeading?: boolean;
  isDrawer?: boolean;
}

const ProfileVisibility = (props: ProfileVisibilityProps) => {
  const { config } = useConfig();
  const {
    onChange,
    privacyLevelMessage,
    value,
    withCardPromote = true,
    showHeading = true,
    isDrawer = false,
  } = props;
  const { options } = useProfileVisibilityOptions();
  const client = useTrackingClient();
  const hubble = useEnhancedHubble();
  const { t } = useTranslations(translations);
  const profilePrivacyUrl = useMelwaysLink('/profile-privacy');

  const handleProfileVisibilityField2Change = (newValue: string) => {
    onChange(newValue);

    // Only track the event when the change is made via the drawer
    if (isDrawer) {
      hubble.trackEvent('component_pressed', {
        component: {
          type: 'radioGroup',
          name: 'set visibility',
          content: newValue.toLowerCase(),
        },
        currentPage: 'apply review',
        displayLanguage: config.LANGUAGE,
        zone: config.ZONE,
      });
    }
  };

  const trackLearnMoreBtnClick = () => {
    hubble.trackEvent('component_pressed', {
      component: {
        type: 'textLink',
        name: 'learn more about visibility',
      },
      displayLanguage: config.LANGUAGE,
      zone: config.ZONE,
    });
  };

  useEffectOnce(() => {
    client.trackLink({
      link: 'profile_section_displayed',
      eventName: ['profile_section_displayed'],
      linkName: 'profile_section_displayed',
      currentPage: Page.REVIEW_PAGE,
      currentPageSection: PageSection.PROFILE_VISIBILITY,
      pageTitle: Page.REVIEW_PAGE,
      currentPageSectionState: value ?? 'not set',
    });
  });

  useSetHubblePage(Page.REVIEW_PAGE);

  useEffect(() => {
    if (privacyLevelMessage) {
      client.trackLink({
        link: 'candidate-did-not-select-profile-visibility',
        pageTitle: Page.REVIEW_PAGE,
        currentPage: Page.REVIEW_PAGE,
        eventName: ['submit_error_viewed'],
      });
    }
  }, [privacyLevelMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const content = (
    <Stack space="large">
      {showHeading && (
        <Heading level="3">
          {t('Maximise your chances', { brand: config.BRAND })}
        </Heading>
      )}
      <Text>{t('Control who can search your profile')}</Text>
      <ProfileVisibilityField2
        errorMessage={privacyLevelMessage}
        onChange={handleProfileVisibilityField2Change}
        options={options}
        value={value}
      />
      <Text size="small" tone="secondary">
        {t('For all settings, your profile including...', {
          TextLink: (children: React.ReactNode[]) => (
            <TextLink
              key="profilePrivacyPath"
              target="_blank"
              href={profilePrivacyUrl}
              hitArea="large"
              onClick={trackLearnMoreBtnClick}
              weight="regular"
              data={{ testid: 'learn-more-privacy-policy' }}
            >
              {children}
            </TextLink>
          ),
        })}
      </Text>
    </Stack>
  );

  if (!withCardPromote) {
    return <Box data={{ testid: 'profile-visibility-content' }}>{content}</Box>;
  }

  return (
    <Card tone="promote" data={{ testid: 'profile-visibility-card' }}>
      {content}
    </Card>
  );
};

export const ProfileVisibilityInput = <TFormValues extends FieldValues>({
  control,
  privacyLevelMessage,
  withCardPromote,
  showHeading,
  isDrawer,
}: {
  control: Control<TFormValues>;
  privacyLevelMessage?: string;
  withCardPromote?: boolean;
  showHeading?: boolean;
  isDrawer?: boolean;
}) => {
  const { t } = useTranslations(translations);

  return (
    <Controller
      control={control}
      render={({ field }) => (
        <ProfileVisibility
          {...field}
          privacyLevelMessage={privacyLevelMessage}
          withCardPromote={withCardPromote}
          showHeading={showHeading}
          isDrawer={isDrawer}
        />
      )}
      name={'profilePrivacyLevel' as Path<TFormValues>}
      rules={{
        required: {
          value: true,
          message: t('You must select profile visibility setting to continue'),
        },
      }}
    />
  );
};
